// src/components/superadmin/Header.jsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  HiOutlineBell, 
  HiOutlineMail, 
  HiOutlineUser, 
  HiMenu,
  HiOutlineLogout,
  HiOutlineCog,
  HiOutlineUserCircle,
  HiOutlineChevronDown,
  HiOutlineCheckCircle,
  HiOutlineXCircle
} from 'react-icons/hi';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';

const Header = ({ title, toggleSidebar }) => {
  const [adminEmail, setAdminEmail] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([
    { id: 1, text: 'New college registration pending', time: '5 min ago', read: false },
    { id: 2, text: 'System update completed', time: '1 hour ago', read: false },
    { id: 3, text: 'Weekly report ready', time: '2 hours ago', read: true },
    { id: 4, text: 'New user registered', time: '3 hours ago', read: true },
  ]);
  const [messages, setMessages] = useState([
    { id: 1, from: 'Admin College', text: 'Need assistance with...', time: '10 min ago', read: false },
    { id: 2, from: 'Support Team', text: 'Your ticket has been...', time: '1 hour ago', read: false },
    { id: 3, from: 'System', text: 'Backup completed', time: '2 hours ago', read: true },
  ]);

  const profileMenuRef = useRef(null);
  const notificationsRef = useRef(null);
  const messagesRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Simulate loading
    setTimeout(() => {
      setAdminEmail(localStorage.getItem('superAdminEmail') || 'superadmin@example.com');
      setLoading(false);
    }, 500);
  }, []);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setShowProfileMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }
      if (messagesRef.current && !messagesRef.current.contains(event.target)) {
        setShowMessages(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const unreadNotifications = notifications.filter(n => !n.read).length;
  const unreadMessages = messages.filter(m => !m.read).length;

  const handleLogout = () => {
    toast.promise(
      new Promise((resolve) => {
        setTimeout(() => {
          Cookies.remove('superAdminToken');
          localStorage.removeItem('superAdminEmail');
          localStorage.removeItem('superAdminId');
          resolve();
        }, 1000);
      }),
      {
        loading: 'Logging out...',
        success: 'Logged out successfully!',
        error: 'Failed to logout',
      }
    ).then(() => {
      navigate('/login');
    });
  };

  const markAllAsRead = (type) => {
    if (type === 'notifications') {
      setNotifications(notifications.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } else {
      setMessages(messages.map(m => ({ ...m, read: true })));
      toast.success('All messages marked as read');
    }
  };

  const dropdownVariants = {
    hidden: { opacity: 0, y: -10, scale: 0.95 },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { type: "spring", damping: 20, stiffness: 300 }
    },
    exit: { 
      opacity: 0, 
      y: -10, 
      scale: 0.95,
      transition: { duration: 0.2 }
    }
  };

  const SkeletonAvatar = () => (
    <div className="w-7 h-7 md:w-8 md:h-8 bg-gray-200 rounded-full animate-pulse" />
  );

  const SkeletonText = () => (
    <div className="hidden sm:block space-y-1">
      <div className="h-3 w-24 bg-gray-200 rounded animate-pulse"></div>
      <div className="h-2 w-16 bg-gray-200 rounded animate-pulse"></div>
    </div>
  );

  return (
    <motion.header
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-white shadow-lg px-3 md:px-4 py-2 md:py-3 flex justify-between items-center sticky top-0 z-30 border-b border-gray-200"
    >
      <div className="flex items-center space-x-2 md:space-x-4">
        {/* Mobile menu button */}
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleSidebar}
          className="md:hidden p-2 hover:bg-gray-100 rounded-lg text-gray-600 transition-colors"
          aria-label="Toggle sidebar"
        >
          <HiMenu className="w-5 h-5" />
        </motion.button>
        
        <div>
          <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-gray-800 truncate max-w-[150px] sm:max-w-[250px] md:max-w-full">
            {title}
          </h1>
          <p className="text-xs text-gray-500 hidden sm:block">
            {new Date().toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })}
          </p>
        </div>
      </div>
      
      <div className="flex items-center space-x-1 sm:space-x-3">
        {/* Messages */}
        <div className="relative" ref={messagesRef}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              setShowMessages(!showMessages);
              setShowNotifications(false);
              setShowProfileMenu(false);
            }}
            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full relative transition-colors"
            aria-label="Messages"
          >
            <HiOutlineMail className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
            <AnimatePresence>
              {unreadMessages > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-gray-600 text-white text-[10px] flex items-center justify-center rounded-full"
                >
                  {unreadMessages}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          <AnimatePresence>
            {showMessages && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-40"
              >
                <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-800">Messages</h3>
                  {unreadMessages > 0 && (
                    <button
                      onClick={() => markAllAsRead('messages')}
                      className="text-xs text-gray-500 hover:text-gray-700 transition"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {messages.length === 0 ? (
                    <div className="p-8 text-center">
                      <HiOutlineMail className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No messages</p>
                    </div>
                  ) : (
                    messages.map((message) => (
                      <motion.div
                        key={message.id}
                        whileHover={{ backgroundColor: '#f9fafb' }}
                        className={`p-3 border-b border-gray-100 cursor-pointer ${!message.read ? 'bg-gray-50' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-sm font-medium text-gray-800">{message.from}</span>
                          <span className="text-xs text-gray-400">{message.time}</span>
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-1">{message.text}</p>
                      </motion.div>
                    ))
                  )}
                </div>

                <div className="p-2 border-t border-gray-200">
                  <button className="w-full text-xs text-center text-gray-500 hover:text-gray-700 py-1 transition">
                    View all messages
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Notifications */}
        <div className="relative" ref={notificationsRef}>
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => {
              setShowNotifications(!showNotifications);
              setShowMessages(false);
              setShowProfileMenu(false);
            }}
            className="p-1.5 md:p-2 hover:bg-gray-100 rounded-full relative transition-colors"
            aria-label="Notifications"
          >
            <HiOutlineBell className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
            <AnimatePresence>
              {unreadNotifications > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  exit={{ scale: 0 }}
                  className="absolute -top-1 -right-1 w-4 h-4 bg-gray-600 text-white text-[10px] flex items-center justify-center rounded-full"
                >
                  {unreadNotifications}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>

          <AnimatePresence>
            {showNotifications && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 w-72 sm:w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-40"
              >
                <div className="p-3 border-b border-gray-200 flex justify-between items-center">
                  <h3 className="font-semibold text-gray-800">Notifications</h3>
                  {unreadNotifications > 0 && (
                    <button
                      onClick={() => markAllAsRead('notifications')}
                      className="text-xs text-gray-500 hover:text-gray-700 transition"
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                <div className="max-h-80 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center">
                      <HiOutlineBell className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <motion.div
                        key={notification.id}
                        whileHover={{ backgroundColor: '#f9fafb' }}
                        className={`p-3 border-b border-gray-100 cursor-pointer ${!notification.read ? 'bg-gray-50' : ''}`}
                      >
                        <div className="flex justify-between items-start mb-1">
                          <span className="text-sm font-medium text-gray-800">System</span>
                          <span className="text-xs text-gray-400">{notification.time}</span>
                        </div>
                        <p className="text-xs text-gray-600 line-clamp-1">{notification.text}</p>
                      </motion.div>
                    ))
                  )}
                </div>

                <div className="p-2 border-t border-gray-200">
                  <button className="w-full text-xs text-center text-gray-500 hover:text-gray-700 py-1 transition">
                    View all notifications
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Profile Menu */}
        <div className="relative" ref={profileMenuRef}>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setShowProfileMenu(!showProfileMenu);
              setShowNotifications(false);
              setShowMessages(false);
            }}
            className="flex items-center space-x-1 sm:space-x-2 border-l border-gray-200 pl-2 sm:pl-4 py-1"
            aria-label="Profile menu"
          >
            {loading ? (
              <SkeletonAvatar />
            ) : (
              <div className="w-7 h-7 md:w-8 md:h-8 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center shadow-md">
                <HiOutlineUser className="w-3 h-3 md:w-4 md:h-4 text-white" />
              </div>
            )}
            
            {loading ? (
              <SkeletonText />
            ) : (
              <div className="hidden sm:block text-left">
                <div className="flex items-center gap-1">
                  <p className="text-xs md:text-sm font-medium text-gray-700 truncate max-w-[100px]">
                    {adminEmail}
                  </p>
                  <HiOutlineChevronDown className={`w-3 h-3 text-gray-500 transition-transform ${showProfileMenu ? 'rotate-180' : ''}`} />
                </div>
                <p className="text-xs text-gray-500">Super Admin</p>
              </div>
            )}
          </motion.button>

          <AnimatePresence>
            {showProfileMenu && (
              <motion.div
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden z-40"
              >
                {/* Profile Header */}
                <div className="p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-gray-700 to-gray-900 rounded-full flex items-center justify-center">
                      <HiOutlineUser className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 truncate">{adminEmail}</p>
                      <p className="text-xs text-gray-500">Super Administrator</p>
                    </div>
                  </div>
                </div>

                {/* Menu Items */}
                <div className="p-2">
                  {/* <motion.button
                    whileHover={{ x: 5 }}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition"
                  >
                    <HiOutlineUserCircle className="w-4 h-4" />
                    <span>Profile</span>
                  </motion.button>

                  <motion.button
                    whileHover={{ x: 5 }}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg transition"
                  >
                    <HiOutlineCog className="w-4 h-4" />
                    <span>Settings</span>
                  </motion.button> */}

                  <div className="border-t border-gray-200 my-2"></div>

                  <motion.button
                    whileHover={{ x: 5 }}
                    onClick={handleLogout}
                    className="w-full flex items-center space-x-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                  >
                    <HiOutlineLogout className="w-4 h-4" />
                    <span>Logout</span>
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.header>
  );
};

export default Header;